import {
  POLICY_BANNER_HIDE_AFTER,
  POLICY_VERSION,
} from "@/lib/config/policy-update.config";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { MdClose } from "react-icons/md";

const DISMISS_KEY = `policy-update-banner-dismissed-${POLICY_VERSION}`;

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Site-wide announcement of the ToS + Privacy Policy update (LUT-941).
 * Auto-hides two weeks after launch; dismissible per browser before that.
 */
export default function PolicyUpdateBanner() {
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (dismissed || Date.now() > POLICY_BANNER_HIDE_AFTER.getTime()) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Best effort — the banner just reappears next visit.
    }
  };

  return (
    <Flex
      align="center"
      py="2"
      className="relative w-full z-[1000] bg-[#F3EEFF] px-[48px]"
      data-testid="policy-update-banner"
    >
      <Text className="w-full text-center text-[13px] leading-[140%] text-[#050C2B]">
        We&apos;ve updated our{" "}
        <Link
          to="/terms"
          className="text-brand-primary underline font-semibold"
        >
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link
          to="/privacy"
          className="text-brand-primary underline font-semibold"
        >
          Privacy Policy
        </Link>
        , effective September 2, 2026.
      </Text>
      <IconButton
        variant="ghost"
        size="1"
        aria-label="Dismiss announcement"
        onClick={handleDismiss}
        data-testid="policy-update-banner-dismiss"
        className="absolute right-[16px] top-1/2 -translate-y-1/2 text-[#050C2B]/60 hover:text-[#050C2B] cursor-pointer"
      >
        <MdClose size={16} />
      </IconButton>
    </Flex>
  );
}
